const EMPTY = '—'

export function formatQuoteLabel(quoteNumber: number, version: number): string {
  return `${quoteNumber} v${version}`
}

export function formatDatePtBr(value: string | null | undefined): string {
  if (!value) return EMPTY
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return EMPTY
  return d.toLocaleDateString('pt-BR')
}

export function formatDateTimePtBr(value: string | null | undefined): string {
  if (!value) return EMPTY
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return EMPTY
  return d.toLocaleString('pt-BR')
}

export function formatCurrencyBRL(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return EMPTY
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function formatIntegerPtBr(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return EMPTY
  return value.toLocaleString('pt-BR')
}

export function formatCommission(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return EMPTY
  const formatted = value.toLocaleString('pt-BR', { maximumFractionDigits: 4 })
  return `${formatted} %`
}

export function formatPercentPtBr(value: number): string {
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

export type QuoteStatusDashboardStyle = {
  iconBg: string
  iconColor: string
  premiumColor: string
}

export function quoteStatusDashboardStyle(statusKey: string): QuoteStatusDashboardStyle {
  switch (statusKey) {
    case 'expirada':
      return {
        iconBg: 'bg-stone-100 dark:bg-stone-800',
        iconColor: 'text-stone-600 dark:text-stone-300',
        premiumColor: 'text-stone-600 dark:text-stone-300',
      }
    case 'enviada':
      return {
        iconBg: 'bg-violet-100 dark:bg-violet-900/40',
        iconColor: 'text-violet-600 dark:text-violet-300',
        premiumColor: 'text-violet-600 dark:text-violet-400',
      }
    case 'aprovada':
      return {
        iconBg: 'bg-green-100 dark:bg-green-900/40',
        iconColor: 'text-green-600 dark:text-green-300',
        premiumColor: 'text-gray-700 dark:text-gray-200',
      }
    case 'recusada':
      return {
        iconBg: 'bg-red-100 dark:bg-red-900/40',
        iconColor: 'text-red-600 dark:text-red-300',
        premiumColor: 'text-red-600 dark:text-red-400',
      }
    case 'em analise':
      return {
        iconBg: 'bg-amber-100 dark:bg-amber-900/40',
        iconColor: 'text-amber-600 dark:text-amber-300',
        premiumColor: 'text-gray-700 dark:text-gray-200',
      }
    case 'cancelada':
      return {
        iconBg: 'bg-slate-100 dark:bg-slate-800',
        iconColor: 'text-slate-600 dark:text-slate-300',
        premiumColor: 'text-gray-700 dark:text-gray-200',
      }
    case 'aberta':
      return {
        iconBg: 'bg-sky-100 dark:bg-sky-900/40',
        iconColor: 'text-sky-600 dark:text-sky-300',
        premiumColor: 'text-sky-600 dark:text-sky-400',
      }
    case 'bloqueada':
      return {
        iconBg: 'bg-orange-100 dark:bg-orange-900/40',
        iconColor: 'text-orange-600 dark:text-orange-300',
        premiumColor: 'text-orange-700 dark:text-orange-400',
      }
    default:
      return {
        iconBg: 'bg-gray-100 dark:bg-gray-700',
        iconColor: 'text-gray-600 dark:text-gray-300',
        premiumColor: 'text-gray-700 dark:text-gray-200',
      }
  }
}

export function truncateWithTooltip(
  text: string | null | undefined,
  max = 25
): { display: string; title: string | undefined } {
  const full = (text ?? '').trim()
  if (!full) return { display: EMPTY, title: undefined }
  if (full.length <= max) return { display: full, title: undefined }
  return { display: `${full.slice(0, max)}…`, title: full }
}

export function normalizeStatusKey(status: string | null | undefined): string {
  return (status ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/** Compara nomes de responsável da cotação (SCGP) com perfis do QG. */
export function normalizeResponsibleKey(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
}

/** Valores especiais do filtro Responsável cotação (Portal / Central). */
export const RESPONSAVEL_FILTER_PORTAL = 'portal'
export const RESPONSAVEL_FILTER_CENTRAL = 'central'

export type ResponsavelCotacaoOrigem =
  | 'all'
  | typeof RESPONSAVEL_FILTER_PORTAL
  | typeof RESPONSAVEL_FILTER_CENTRAL

export function isPortalResponsible(value: string | null | undefined): boolean {
  return normalizeResponsibleKey(value) === RESPONSAVEL_FILTER_PORTAL
}

/**
 * Filtro em cascata: origem Todos | Portal | Central.
 * Central = tudo que não é Portal (inclui null/vazio).
 * Com pessoasCentrais preenchido, restringe às chaves normalizadas selecionadas.
 */
export function quoteMatchesResponsavelFilter(
  responsible: string | null | undefined,
  origem: ResponsavelCotacaoOrigem | string,
  pessoasCentrais: string[] = []
): boolean {
  if (!origem || origem === 'all') return true
  const portal = isPortalResponsible(responsible)
  if (origem === RESPONSAVEL_FILTER_PORTAL) return portal
  if (origem === RESPONSAVEL_FILTER_CENTRAL) {
    if (portal) return false
    if (pessoasCentrais.length === 0) return true
    const key = normalizeResponsibleKey(responsible)
    return pessoasCentrais.includes(key)
  }
  return true
}

/**
 * Padroniza o rótulo do responsável na UI (filtro).
 * Nomes só em maiúsculas/minúsculas (ex.: SCGP) viram Title Case;
 * mistos (ex.: perfil QG) permanecem como estão.
 */
export function formatResponsibleDisplayName(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim().replace(/\s+/g, ' ')
  if (!trimmed) return ''

  const letters = trimmed.replace(/[^\p{L}]/gu, '')
  if (!letters) return trimmed

  const lower = letters.toLocaleLowerCase('pt-BR')
  const upper = letters.toLocaleUpperCase('pt-BR')
  const isUniformCase = letters === lower || letters === upper
  if (!isUniformCase) return trimmed

  return trimmed
    .toLocaleLowerCase('pt-BR')
    .replace(/(^|[\s'’-])(\p{L})/gu, (_, sep: string, ch: string) => `${sep}${ch.toLocaleUpperCase('pt-BR')}`)
}

export function quoteStatusBadgeClass(status: string | null | undefined): string {
  const key = normalizeStatusKey(status)
  switch (key) {
    case 'aberta':
      return 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-800'
    case 'aprovada':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800'
    case 'bloqueada':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800'
    case 'cancelada':
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'
    case 'em analise':
      return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800'
    case 'enviada':
      return 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-800'
    case 'expirada':
      return 'bg-stone-200 text-stone-800 border-stone-300 dark:bg-stone-800 dark:text-stone-200 dark:border-stone-700'
    case 'recusada':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'
  }
}

export function formatPhoneBR(phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D/g, '')
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  const trimmed = (phone ?? '').trim()
  return trimmed || EMPTY
}

export function getClientDisplayName(quote: {
  company?: string | null
  requester_name?: string | null
}): string {
  const company = (quote.company ?? '').trim()
  if (company) return company
  const requester = (quote.requester_name ?? '').trim()
  return requester || EMPTY
}
